import { useState, useCallback } from 'react';
import type { LikeTargetType, LikeResponse } from '../types';
import * as socialApi from '../services/social-api';

export interface UseLikesReturn {
  isLiked: boolean;
  likesCount: number;
  isLoading: boolean;
  error: string | null;
  toggleLike: () => Promise<void>;
}

/**
 * Hook for managing like state on a trip or pin.
 */
export function useLikes(
  targetType: LikeTargetType,
  targetId: string,
  initialIsLiked = false,
  initialCount = 0,
): UseLikesReturn {
  const [isLiked, setIsLiked] = useState(initialIsLiked);
  const [likesCount, setLikesCount] = useState(initialCount);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleLike = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      let result: LikeResponse;
      if (isLiked) {
        result = await socialApi.unlikeTarget(targetType, targetId);
      } else {
        result = await socialApi.likeTarget(targetType, targetId);
      }
      setIsLiked(result.liked);
      setLikesCount(result.likesCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle like');
    } finally {
      setIsLoading(false);
    }
  }, [isLiked, targetType, targetId]);

  return { isLiked, likesCount, isLoading, error, toggleLike };
}
